import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import api from "../../api";
import StatsModal from "../../components/Common/StatsModal";
import { Container, Button } from "reactstrap";
import "../Items/ManageItem.css";

const PlayerBoxView = ({ username, embedded = false }) => {
  const [pokemonList, setPokemonList] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [isSearching, setIsSearching] = useState(false);



  // ─── Stats–Modal State & Helpers ───────────────────────────────
  const [statsModal, setStatsModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const toggleStatsModal = () => setStatsModal(!statsModal);

  const openStatsModal = (ownedId) => {
    setModalLoading(true);
    setModalData(null);
    setStatsModal(true);

    api
      .get(`/v1/pokemon/${ownedId}/stats`, { withCredentials: true })
      .then((res) => setModalData(res.data))
      .catch((err) => {
        console.error("Failed to load stats:", err);
        setModalData({ error: "Could not load data." });
      })
      .finally(() => setModalLoading(false));
  };

  function getLevel(exp) {
    const lvl = Math.floor(Math.pow(exp, 1 / 3));
    return Math.pow(lvl + 1, 3) <= exp ? lvl + 1 : lvl;
  }

  const getLevelColor = (level) => {
    if (level < 59) return '#14b8a6'; // teal for <59
    if (level < 100) return '#ef4444'; // red for 59-99
    return '#FFD700'; // gold for 100+
  };

  const fetchPokemon = React.useCallback((page = 1, pageSize = rowsPerPage) => {
    setLoading(true);
    api
      .get("/v1/pokemon/box-view", {
        params: {
          username,
          page,
          limit: pageSize,
          type: typeFilter || undefined,
        },
      })
      .then((res) => {
        setPokemonList(res.data.data);
        setTotalRows(res.data.total);  
        setCurrentPage(res.data.page);
        setLoading(false);
      })
      
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [rowsPerPage, typeFilter, username]);

  const searchPokemon = (query) => {
    if (!query) {
      setIsSearching(false);
      setCurrentPage(1);
      fetchPokemon(1, rowsPerPage);
      return;
    }
  
    setIsSearching(true);
    setLoading(true);
    api
      .get("/v1/pokemon/box-search", {
        params: { username, query },
      })
      .then((res) => {
        setPokemonList(res.data); 
        setTotalRows(res.data.length);
        setCurrentPage(1);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Search failed:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!isSearching) {
      fetchPokemon(currentPage, rowsPerPage);
    }
  }, [fetchPokemon, currentPage, isSearching, rowsPerPage]);
  // Derived pagination and displayed list
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage + 1;
  const endIndex = Math.min(totalRows, currentPage * rowsPerPage);
  const displayedList = isSearching
    ? pokemonList.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : pokemonList;

  const content = (
    <Container fluid>
      {!embedded && (
        <Breadcrumbs
          title={`Viewing ${username}`}
          breadcrumbItem="Player Box"
        />
      )}

      <div className="equipment-container">
        <div className="equipment-header">
          <h1 className="equipment-title">Box</h1>
          <p className="equipment-subtitle">Viewing {username}'s stored Pokémon</p>
        </div>

        <div className="inventory-section">
              <div className="inventory-header" style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="search-container" style={{ flex: '1 1 320px' }}>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search Pokémon..."
                    value={search}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearch(value);
                      if (value.trim() === "") {
                        setIsSearching(false);
                        setCurrentPage(1);
                        fetchPokemon(1, rowsPerPage);
                      } else {
                        setIsSearching(true);
                        searchPokemon(value);
                      }
                    }}
                  />
                  <div className="search-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="21 21l-4.35-4.35"></path>
                    </svg>
                  </div>
                </div>

                <select
                  className="form-select"
                  style={{ maxWidth: '200px', background: '#121826', color: '#e2e8f0', borderColor: '#334155' }}
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Types</option>
                  <option value="Diamond">Diamond</option>
                  <option value="Emerald">Emerald</option>
                  <option value="Golden">Golden</option>
                  <option value="Ruby">Ruby</option>
                  <option value="Sapphire">Sapphire</option>
                  <option value="Shadow">Shadow</option>
                  <option value="Shiny">Shiny</option>
                </select>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#b8c5d6' }}>Rows:</span>
                  <select
                    className="form-select"
                    style={{ width: 100, background: '#121826', color: '#e2e8f0', borderColor: '#334155' }}
                    value={rowsPerPage}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value, 10);
                      setRowsPerPage(newLimit);
                      setCurrentPage(1);
                    }}
                  >
                    <option value={12}>12</option>
                    <option value={20}>20</option>
                    <option value={40}>40</option>
                    <option value={60}>60</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="inventory-grid">
                {displayedList.map((mon) => {
                  const level = getLevel(mon.totalexp);
                  return (
                    <div
                      key={mon.id}
                      className="inventory-item"
                      onClick={() => openStatsModal(mon.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img
                        src={`/icon/${mon.name}.png`}
                        alt={mon.nickname || mon.name}
                        className="inventory-item-image"
                      />
                      <div className="inventory-item-name">{mon.nickname || mon.name}</div>
                      <div
                        className="inventory-item-quantity"
                        style={{ background: getLevelColor(level), color: '#fff' }}
                      >
                        Lvl {level.toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {pokemonList.length === 0 && (
                <div style={{ textAlign: 'center', color: '#b8c5d6', padding: '40px' }}>
                  <p>{loading ? 'Loading…' : 'No Pokémon found'}</p>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
                <div style={{ color: '#b8c5d6' }}>
                  Showing {startIndex.toLocaleString()}–{endIndex.toLocaleString()} of {totalRows.toLocaleString()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button color="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
                    First
                  </Button>
                  <Button color="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    Prev
                  </Button>
                  <span style={{ color: '#b8c5d6' }}>Page {currentPage} / {totalPages}</span>
                  <Button color="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    Next
                  </Button>
                  <Button color="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>
                    Last
                  </Button>
                </div>
              </div>
            </div>
          </div>
    </Container>
  );

  return (
    <React.Fragment>
      {!embedded ? (
        <div className="page-content">
          {content}
        </div>
      ) : (
        content
      )}

      {/* ─── Stats Modal ──────────────────────────────────────────── */}
      <StatsModal 
        isOpen={statsModal} 
        toggle={toggleStatsModal} 
        loading={modalLoading} 
        data={modalData} 
      />
    </React.Fragment>
  );
};

export default PlayerBoxView;
